import { useId, type ReactElement } from 'react';
import {
  GALLEY_WEIGHT_KG,
  IN_SEAT_POWER_WEIGHT_KG_PER_SEAT,
  WIFI_HARDWARE_WEIGHT_KG,
} from '../../../domain/economics';
import type { CabinConfiguration } from '../../../domain/models';
import { formatInt, formatKg } from './cabinFormat';

/** Largest galley count the stepper offers. */
export const MAX_GALLEYS = 16;

export interface AmenityControlsProps {
  readonly cabin: CabinConfiguration;
  readonly totalSeats: number;
  /** Galleys the domain recommends for this layout. */
  readonly recommendedGalleys: number;
  readonly onWifiChange: (value: boolean) => void;
  readonly onPowerChange: (value: boolean) => void;
  readonly onGalleysChange: (value: number) => void;
}

interface ToggleRowProps {
  readonly label: string;
  readonly description: string;
  readonly checked: boolean;
  readonly onChange: (value: boolean) => void;
}

function ToggleRow({ label, description, checked, onChange }: ToggleRowProps): ReactElement {
  const uid = useId();
  return (
    <div className="ce-toggle">
      <div className="ce-toggle__text">
        <label className="ce-toggle__label" htmlFor={`${uid}-switch`}>
          {label}
        </label>
        <p className="ce-toggle__desc" id={`${uid}-desc`}>
          {description}
        </p>
      </div>
      <input
        id={`${uid}-switch`}
        className="ce-switch"
        type="checkbox"
        role="switch"
        checked={checked}
        aria-describedby={`${uid}-desc`}
        onChange={(event): void => onChange(event.target.checked)}
      />
    </div>
  );
}

/** Wi-Fi, in-seat power and galley controls. */
export function AmenityControls({
  cabin,
  totalSeats,
  recommendedGalleys,
  onWifiChange,
  onPowerChange,
  onGalleysChange,
}: AmenityControlsProps): ReactElement {
  const galleys = Number.isFinite(cabin.galleyCount) ? cabin.galleyCount : 0;
  const powerKg = IN_SEAT_POWER_WEIGHT_KG_PER_SEAT * totalSeats;
  const underProvisioned = galleys < recommendedGalleys;

  return (
    <section className="ce-card" aria-labelledby="ce-amenities-title">
      <h3 id="ce-amenities-title" className="ce-card__title">
        Amenities &amp; galleys
      </h3>

      <ToggleRow
        label="Wi-Fi hardware"
        description={`+${formatKg(WIFI_HARDWARE_WEIGHT_KG)} of satcom equipment. Raises comfort and fare tolerance.`}
        checked={cabin.wifiEquipped}
        onChange={onWifiChange}
      />
      <ToggleRow
        label="In-seat power"
        description={`+${IN_SEAT_POWER_WEIGHT_KG_PER_SEAT} kg per seat (${formatKg(powerKg)} for this cabin). Raises comfort and fare tolerance.`}
        checked={cabin.inSeatPower}
        onChange={onPowerChange}
      />

      <div className="ce-galleys">
        <div className="ce-toggle__text">
          <span className="ce-toggle__label" id="ce-galley-label">
            Galley modules
          </span>
          <p className="ce-toggle__desc">
            {formatKg(GALLEY_WEIGHT_KG)} each. Recommended for this cabin:{' '}
            <strong>{formatInt(recommendedGalleys)}</strong>
          </p>
        </div>
        <div className="ce-stepper" role="group" aria-labelledby="ce-galley-label">
          <button
            type="button"
            className="ce-btn ce-btn--icon"
            aria-label="Remove a galley"
            disabled={galleys <= 0}
            onClick={(): void => onGalleysChange(Math.max(0, galleys - 1))}
          >
            −
          </button>
          <output
            className={`ce-stepper__value${underProvisioned ? ' ce-stepper__value--warn' : ''}`}
            aria-live="polite"
          >
            {formatInt(galleys)}
          </output>
          <button
            type="button"
            className="ce-btn ce-btn--icon"
            aria-label="Add a galley"
            disabled={galleys >= MAX_GALLEYS}
            onClick={(): void => onGalleysChange(Math.min(MAX_GALLEYS, galleys + 1))}
          >
            +
          </button>
          <button
            type="button"
            className="ce-btn ce-btn--ghost"
            disabled={galleys === recommendedGalleys}
            onClick={(): void => onGalleysChange(recommendedGalleys)}
          >
            Use recommended
          </button>
        </div>
      </div>
    </section>
  );
}
